import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/brand";
import ProductModal from "@/components/ProductModal";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onCardMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="shop" data-testid="product-grid-shop-section" className="relative mx-auto max-w-7xl px-4 sm:px-8 py-28 sm:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 flex flex-col gap-8"
      >
        <p className="text-[10px] tracking-[0.45em] text-zinc-600 uppercase">Ghetto Diaries 1 — Sealed Vaults 001 &amp; 002</p>
        <h2 className="font-display text-5xl sm:text-7xl chrome-text uppercase">The Armoury</h2>
      </motion.div>

      {loading ? (
        <div className="py-24 text-center text-xs tracking-[0.4em] text-zinc-600 uppercase" data-testid="shop-loading">
          Loading the vault…
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
          {products.map((p, i) => (
            <motion.article
              layout
              key={p.slug}
              data-testid={`product-card-${p.slug}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
              onMouseMove={onCardMove}
              onClick={() => setSelected(p)}
              className="spotlight-card group cursor-pointer border border-zinc-800/80 bg-[#111113] overflow-hidden"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/80 via-transparent to-transparent" />
                <span className="absolute top-3 left-3 bg-zinc-950/80 backdrop-blur border border-zinc-700/60 px-2.5 py-1 text-[9px] tracking-[0.3em] text-zinc-400 uppercase">
                  {p.tag}
                </span>
                {p.coming_soon && (
                  <span data-testid={`coming-soon-${p.slug}`} className="absolute top-3 right-3 bg-red-950/85 backdrop-blur border border-red-900/70 px-2.5 py-1 text-[9px] tracking-[0.3em] text-red-200 uppercase">
                    Locked
                  </span>
                )}
                <span className="absolute bottom-3 right-3 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 bg-zinc-100 text-zinc-950 px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase">
                  Quick View
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 p-4">
                <div>
                  <h3 className="text-sm font-bold tracking-wider text-zinc-100 uppercase">{p.name}</h3>
                  <p className="mt-1 text-[10px] tracking-[0.25em] text-zinc-500 uppercase">{p.category}</p>
                  {p.coming_soon && p.reserved_count > 0 && (
                    <p data-testid={`inline-count-${p.slug}`} className="mt-1.5 text-[10px] tracking-[0.25em] text-red-300/90 uppercase">
                      {p.reserved_count} already in line
                    </p>
                  )}
                </div>
                <span className="font-display text-xl chrome-text-bright">
                  {p.coming_soon ? (
                    <span className="text-[9px] tracking-[0.3em] text-zinc-600 uppercase">Sealed</span>
                  ) : (
                    `$${p.price.toFixed(0)}`
                  )}
                </span>
              </div>
            </motion.article>
          ))}
        </motion.div>
      )}

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
